import { useHandler } from "@redotech/react-util/hook";
import ArrowLeft from "@redotech/redo-icons/arrow-left.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import * as darkModeCss from "@redotech/redo-web/theme/component-colors-dark.module.css";
import { nativeRandom, randomHexString } from "@redotech/util/random";
import { memo, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useRouteError } from "react-router-dom";
import notFoundImageUrl from "./404-image.jpg?url";
import * as css from "./merchant-app-404-page.module.css";
import { RouteStateMarker } from "./route-state-marker";

export const MerchantApp404Page = memo(function MerchantApp404Page() {
  const location = useLocation();
  const navigate = useNavigate();
  const error = useRouteError();

  const errorId = useMemo(() => randomHexString(nativeRandom, 8), []);

  const isNotFoundError = (error as any)?.statusText === "Not Found";

  useEffect(() => {
    if (error) {
      console.error(`An error occurred with ID ${errorId}:`, error);
    }
    if (isNotFoundError) {
      console.error(
        `Route not found at location with error id ${errorId}:`,
        location.pathname,
      );
    }
  }, [isNotFoundError, location.pathname, error, errorId]);

  const handleGoBack = useHandler(() => {
    navigate(-1);
  });

  return (
    <div className={css.container}>
      <RouteStateMarker
        path={location.pathname}
        state={isNotFoundError ? "not-found" : "error"}
      />
      <img
        alt="404 Error"
        className={css.backgroundImage}
        src={notFoundImageUrl}
      />
      <Flex className={darkModeCss.darkMode} dir="column" style={{ zIndex: 2 }}>
        <h1 className={css.title}>Lost in the void</h1>
        <Text fontSize="md" textColor="primary">
          {isNotFoundError
            ? "404: The page you were looking for could not be found."
            : "An unexpected error occurred."}
        </Text>
        <Text fontSize="sm" textColor="secondary">
          Please contact support if the problem persists. (Error ID: {errorId})
        </Text>
        <Flex align="flex-start" dir="column" gap="none">
          <RedoButton
            hierarchy="secondary"
            IconLeading={ArrowLeft}
            onClick={handleGoBack}
            size="xl"
            text="Go back"
          />
        </Flex>
      </Flex>
    </div>
  );
});
